<script lang="ts" setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import SidebarLayout from "@/components/layout/SidebarLayout.vue";
import SidebarToggle from "@/components/buttons/SidebarToggle.vue";
import NotFound from "@/views/notFound/NotFound.vue";
import OrderViewInfo from "@/views/orders/view/components/OrderViewInfo.vue";
import OrderViewStops from "@/views/orders/view/components/OrderViewStops.vue";
import OmniTabs from "@/components/omni/OmniTabs.vue";
import OrderFiles from "@/views/orders/view/components/OrderFiles.vue";
import OrderViewSidebar from "@/views/orders/view/components/OrderViewSidebar.vue";
import OmniTooltip from "@/components/omni/OmniTooltip.vue";
import { useBaseView, useNotFound } from "@/composables";
import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import { isSameEntity } from "@/utils/sameEntity";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto";
import { OrderViewTab, OrderViewTabHash } from "@/constants/order";
import OrderFinances from "@/views/orders/view/components/OrderFinances.vue";

import type { OrderExpense, OrderPayment, OrderWithTrip } from "@/api/orders/dto/order.dto";
import type { TripOverview, TripOverviewRoute } from "@/api/trips/dto/trip.dto";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { hasAccess, setTitle } = useBaseView();
const { entityNotFound, validateEntityResponse } = useNotFound();
const snackbarStore = useSnackbarStore();

const api = new API();

const order = ref<OrderWithTrip | null>(null);
const tab = ref<OrderViewTab>(OrderViewTab.overview);

const editLink = computed(() => {
  const name = route.name;

  if (typeof name !== "string" || !order.value) {
    return undefined;
  }

  return {
    name: name.replace("view", "edit"),
    params: { id: order.value.id },
  };
});

const hasAccessToEdit = computed<boolean>(() => {
  return hasAccess(DispatcherPermissionValue.OrdersEdit);
});

const isSidebarOpenDefault = computed<boolean>(() => {
  return window.innerWidth > 1250;
});

const relatedTravelOrders = computed(() => {
  return (
    order.value?.trip?.routes.flatMap((route) => {
      return route.travelOrders.filter((to) => to.orderId === order.value?.id);
    }) || []
  );
});

async function loadRouteTruck(route: TripOverviewRoute): Promise<TripOverviewRoute> {
  if (!route.truckId) {
    return route;
  }

  const truckResp = await api.trucks.findTruckById(route.truckId);
  return { ...route, truck: truckResp.data };
}

async function loadTrip(tripId: number): Promise<TripOverview | null> {
  const tripResp = await api.trips.getTripById(tripId);

  if (!tripResp.success || !tripResp.data) {
    snackbarStore.showSnackbarError(t("orders.trip-loading-error"));
    return null;
  }

  const routes = await Promise.all(tripResp.data.routes.map(loadRouteTruck));
  return { ...tripResp.data, routes };
}

async function loadOrder() {
  try {
    const orderResp = await api.orders.findOrderById(String(route.params.id));

    if (!validateEntityResponse(orderResp) || !orderResp.data) {
      return;
    }

    const trip = orderResp.data.tripId ? await loadTrip(orderResp.data.tripId) : null;

    order.value = { ...orderResp.data, trip: trip ?? { routes: [] } };
  } catch (error) {
    console.error(error);
  }
}

function onSuccessPaymentCreation(payment: OrderPayment) {
  if (!order.value) {
    return;
  }

  order.value.orderPayments.push(payment);
}

function onSuccessPaymentUpdate(payment: OrderPayment) {
  if (!order.value) {
    return;
  }

  order.value.orderPayments = order.value.orderPayments.map((orderPayment) => {
    return isSameEntity(orderPayment, payment) ? payment : orderPayment;
  });
}

function onSuccessPaymentDelete(payment: OrderPayment) {
  if (!order.value) {
    return;
  }

  order.value.orderPayments = order.value.orderPayments.filter((orderPayment) => {
    return !isSameEntity(orderPayment, payment);
  });
}

function onSuccessExpenseCreation(expense: OrderExpense) {
  if (!order.value) {
    return;
  }

  order.value.expenses = [...(order.value.expenses ?? []), expense];
}

function onSuccessExpenseUpdate(expense: OrderExpense) {
  if (!order.value) {
    return;
  }

  order.value.expenses = (order.value.expenses ?? []).map((orderExpense) => {
    return orderExpense.id === expense.id ? expense : orderExpense;
  });
}

function onSuccessExpenseDelete(expense: OrderExpense) {
  if (!order.value) {
    return;
  }

  order.value.expenses = (order.value.expenses ?? []).filter((orderExpense) => {
    return orderExpense.id !== expense.id;
  });
}

function setTabByHash(hash: string) {
  const tabHash = hash.replace("#", "");

  tab.value =
    Object.values(OrderViewTab).find((value) => {
      return OrderViewTabHash[value] === tabHash;
    }) ?? OrderViewTab.overview;
}

onMounted(async () => {
  await loadOrder();

  if (!order.value) {
    return;
  }

  setTitle(t("orders.order-number", { number: order.value.number }), t("orders.orders"));
});

watch(tab, (value) => {
  const hash = OrderViewTabHash[value];

  router.replace({ hash: hash ? `#${hash}` : "" });
});

watch(
  () => route.hash,
  (hash) => {
    setTabByHash(hash);
  },
  { immediate: true },
);
</script>

<template>
  <v-container v-if="!entityNotFound" :fluid="true" class="pt-6 pb-10 pl-6 pr-6 height-100 users-page">
    <sidebar-layout :sidebar-opened="isSidebarOpenDefault">
      <div class="w-100">
        <div class="d-flex justify-space-between align-center flex-wrap mb-4">
          <div class="text-grey-darken-4 title font-weight-medium font-size-20">
            {{ t("orders.order-number", { number: order?.number ?? "" }) }}
          </div>
          <div v-if="order?.id" class="d-flex align-center">
            <omni-tooltip :disabled="hasAccessToEdit">
              <template #activator>
                <v-btn
                  :disabled="!hasAccessToEdit"
                  :to="editLink"
                  class="text-uppercase font-weight-600 text-white mr-2"
                  color="primary"
                  variant="flat"
                >
                  <v-icon class="mr-2" size="20"> mdi-pencil-outline</v-icon>
                  {{ t("orders.edit-order") }}
                </v-btn>
              </template>

              {{ t("permission.edit-order") }}
            </omni-tooltip>
            <sidebar-toggle class="mr-2"></sidebar-toggle>
          </div>
        </div>

        <v-card v-if="order" variant="flat">
          <omni-tabs v-model="tab" class="rounded">
            <v-tab :value="OrderViewTab.overview" class="text-uppercase">{{ t("orders.tab-overview") }}</v-tab>
            <v-tab :value="OrderViewTab.finances" class="text-uppercase">{{ t("orders.finances") }}</v-tab>
            <v-tab :value="OrderViewTab.updates" class="text-uppercase">{{ t("orders.tab-updates") }}</v-tab>
            <v-tab :value="OrderViewTab.files" class="text-uppercase">{{ t("orders.tab-files") }}</v-tab>
          </omni-tabs>

          <v-window v-model="tab" :touch="false">
            <v-window-item :value="OrderViewTab.overview">
              <order-view-info :order="order"></order-view-info>
              <order-view-stops :order="order"></order-view-stops>
            </v-window-item>

            <v-window-item :value="OrderViewTab.finances">
              <order-finances
                :order="order"
                :travel-orders="relatedTravelOrders"
                @succcess-create-payment="onSuccessPaymentCreation"
                @success-edit-payment="onSuccessPaymentUpdate"
                @success-delete-payment="onSuccessPaymentDelete"
                @success-create-expense="onSuccessExpenseCreation"
                @success-edit-expense="onSuccessExpenseUpdate"
                @success-delete-expense="onSuccessExpenseDelete"
              />
            </v-window-item>

            <v-window-item :value="OrderViewTab.updates"></v-window-item>

            <v-window-item :value="OrderViewTab.files">
              <order-files :order="order"></order-files>
            </v-window-item>
          </v-window>
        </v-card>
      </div>
      <template #sidebar>
        <order-view-sidebar v-if="order" :order="order"></order-view-sidebar>
      </template>
    </sidebar-layout>
  </v-container>
  <not-found v-else :text="t('orders.order-not-found')" icon="mdi-account-remove-outline"></not-found>
</template>

<style lang="scss" scoped>
b {
  color: black;
}

h3 {
  margin: 0 0 3px;
  color: black;
}

h4 {
  margin: 0 0 2px;
  color: black;
}

.v-card {
  background-color: transparent !important;
}
</style>
